import {
  Box,
  BoxProps,
  Divider,
  Flex,
  IconButton,
  SlideFade,
  Stack,
  Text,
} from '@chakra-ui/react';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '$/components/common/Icon';
import { ThemeSwitcher } from '$/components/common/ThemeSwitcher';
import { useAuthorization } from '$/components/core/Authentication/hooks/useAuthorization';
import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { BrandLink } from '$/components/core/BrandLink';
import { NavbarButton } from '$/components/layouts/BaseDashboardLayout/components/MobileHeader/components/NavbarButton';
import { NavbarLabel } from '$/components/layouts/BaseDashboardLayout/components/MobileHeader/components/NavbarLabel';
import { NavbarLink } from '$/components/layouts/BaseDashboardLayout/components/NavbarLink';
import { NavbarLinks } from '$/components/layouts/BaseDashboardLayout/components/NavbarLinks';

export const MobileHeader: FC<BoxProps> = (props) => {
  const { t } = useTranslation();
  const { hasPermission } = useAuthorization();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [renderMenuContent, setRenderMenuContent] = useState(false);
  const onOpenAuthenticationModal = useAuthenticationStore.useOnModalOpen();

  return (
    <Flex pos='relative' flex='1'>
      <Flex
        as='header'
        layerStyle='DashboardSection'
        align='center'
        justify='space-between'
        w='full'
        h='56px'
        px='18px'
        {...props}
      >
        <BrandLink />
        <IconButton
          aria-label={t('dashboard.header.openMenu')}
          icon={
            isMenuOpen ? <Icon icon='close' w='14px' /> : <Icon icon='burger' />
          }
          onClick={() => setIsMenuOpen((state) => !state)}
          variant='ghost'
        />
      </Flex>

      <Box
        pos='absolute'
        zIndex='popover'
        bottom='-8px'
        w='full'
        transform='translateY(100%)'
      >
        <SlideFade
          in={isMenuOpen}
          offsetY='-8px'
          onAnimationComplete={() => {
            if (!isMenuOpen) setRenderMenuContent(false);
          }}
          onAnimationStart={() => {
            if (isMenuOpen) setRenderMenuContent(true);
          }}
        >
          {renderMenuContent && (
            <Stack layerStyle='DashboardSection' gap='0' overflow='hidden'>
              <NavbarLinks onClick={() => setIsMenuOpen(false)} />
              <Divider mt='8px' mb='24px' />
              <NavbarLabel label={t('dashboard.header.manageAccount')} />
              {hasPermission('General_User_Profile') && (
                <NavbarLink
                  icon={<Icon icon='account' />}
                  title={t('dashboard.authentication.profile.yourProfile')}
                  to='/profile'
                  onClick={() => setIsMenuOpen(false)}
                />
              )}
              {hasPermission('General_Abo_Page') && (
                <NavbarLink
                  icon={<Icon icon='leaderboard' />}
                  title={t('dashboard.header.subscriptions')}
                  to='/pricing'
                  onClick={() => setIsMenuOpen(false)}
                />
              )}

              {hasPermission('General_Login_Register') && (
                <NavbarButton
                  onClick={() => {
                    setIsMenuOpen(false);
                    onOpenAuthenticationModal('login');
                  }}
                  icon={<Icon icon='arrow_right' width='13px' />}
                  title={t('dashboard.header.signIn')}
                />
              )}

              <Divider mt='8px' mb='20px' />
              <Flex align='center' justify='space-between' w='full' px='4'>
                <Text fontSize='md' fontWeight='bold'>
                  {t('dashboard.header.theme')}
                </Text>
                <Box h='32px'>
                  <ThemeSwitcher />
                </Box>
              </Flex>
              <Divider mt='20px' mb='24px' />
              <NavbarLabel label={t('general.additional')} />
              <NavbarButton
                onClick={() => setIsMenuOpen(false)}
                icon={<Icon icon='info' width='13px' />}
                title={t('dashboard.header.help')}
              />
              <NavbarButton
                onClick={() => setIsMenuOpen(false)}
                icon={<Icon icon='arrow_right' width='13px' />}
                title={t('dashboard.header.languages')}
              />
              <Divider mt='8px' mb='20px' />
              <NavbarLabel label={t('dashboard.header.legal')} />
              <NavbarLink
                icon={<Icon icon='leaderboard' />}
                title={t('dashboard.header.imprint')}
                to='/imprint'
                onClick={() => setIsMenuOpen(false)}
              />
              <NavbarLink
                icon={<Icon icon='leaderboard' />}
                title={t('dashboard.header.privacy')}
                to='/privacy'
                onClick={() => setIsMenuOpen(false)}
              />
              <NavbarLink
                icon={<Icon icon='leaderboard' />}
                title={t('dashboard.header.terms')}
                to='/not-allowed'
                onClick={() => setIsMenuOpen(false)}
              />
            </Stack>
          )}
        </SlideFade>
      </Box>
    </Flex>
  );
};
